import { useMemo } from 'react';
import { T, useGT } from 'gt-react';
import { PlusIcon, XIcon } from '@phosphor-icons/react';
import {
  toGroupedFilters,
  type EmailRuleBooleanOperator,
  type EmailRuleCondition,
  type EmailRuleConditionGroup,
  type EmailRuleConditionMatchType,
  type EmailRuleFilters,
  type GroupedEmailRuleFilters,
} from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';

// The editor offers a subset of the contract's condition fields (no bcc),
// matching the fields the legacy editor exposed.
export type FilterableField = 'from' | 'to' | 'cc' | 'subject' | 'mailbox';

export const FILTERABLE_FIELDS: ReadonlyArray<{ key: FilterableField }> = [
  { key: 'from' },
  { key: 'to' },
  { key: 'cc' },
  { key: 'subject' },
  { key: 'mailbox' },
];

export function useFieldLabels(): Record<FilterableField, string> {
  const gt = useGT();
  return useMemo(
    () => ({
      from: gt('From'),
      to: gt('To'),
      cc: gt('CC'),
      subject: gt('Subject'),
      mailbox: gt('Mailbox'),
    }),
    [gt],
  );
}

/**
 * Normalizes editor output (or any stored filters) to the canonical grouped
 * shape: convert via {@link toGroupedFilters}, trim condition values, drop
 * conditions with empty/whitespace values, and drop emptied groups.
 */
export function normalizeFilters(filters: EmailRuleFilters): EmailRuleFilters {
  const grouped = toGroupedFilters(filters);
  const groups: EmailRuleConditionGroup[] = [];
  for (const group of grouped.groups) {
    const conditions = group.conditions
      .map((c) => ({ ...c, value: c.value.trim() }))
      .filter((c) => c.value.length > 0);
    if (conditions.length > 0) {
      groups.push({ operator: group.operator, conditions });
    }
  }
  return {
    operator: grouped.operator,
    groups,
    hasAttachments: grouped.hasAttachments ?? null,
  };
}

function makeCondition(field: FilterableField = 'from'): EmailRuleCondition {
  return { field, matchType: 'contains', value: '' };
}

function isFilterableField(value: string): value is FilterableField {
  return FILTERABLE_FIELDS.some(({ key }) => key === value);
}

function isMatchType(value: string): value is EmailRuleConditionMatchType {
  return value === 'contains' || value === 'notContains';
}

function isBooleanOperator(value: string): value is EmailRuleBooleanOperator {
  return value === 'and' || value === 'or';
}

export function EmailRuleFilterEditor({
  filters,
  onChange,
}: {
  filters: EmailRuleFilters;
  onChange: (filters: EmailRuleFilters) => void;
}) {
  const gt = useGT();
  const fieldLabels = useFieldLabels();

  // Derive editing state from props (no useEffect). The grouped shape is the
  // single source of truth; callers re-render us with the value we emit.
  const grouped = toGroupedFilters(filters);
  const { operator, groups } = grouped;
  const hasAttachments = grouped.hasAttachments ?? null;

  const emit = (next: GroupedEmailRuleFilters) => {
    onChange({
      operator: next.operator,
      groups: next.groups,
      hasAttachments: next.hasAttachments ?? null,
    });
  };

  const setTopOperator = (value: EmailRuleBooleanOperator) => {
    emit({ ...grouped, operator: value });
  };

  const setGroupOperator = (
    groupIndex: number,
    value: EmailRuleBooleanOperator,
  ) => {
    emit({
      ...grouped,
      groups: groups.map((g, i) =>
        i === groupIndex ? { ...g, operator: value } : g,
      ),
    });
  };

  const updateCondition = (
    groupIndex: number,
    conditionIndex: number,
    updates: Partial<EmailRuleCondition>,
  ) => {
    emit({
      ...grouped,
      groups: groups.map((g, i) =>
        i === groupIndex
          ? {
              ...g,
              conditions: g.conditions.map((c, ci) =>
                ci === conditionIndex ? { ...c, ...updates } : c,
              ),
            }
          : g,
      ),
    });
  };

  const addCondition = (groupIndex: number) => {
    emit({
      ...grouped,
      groups: groups.map((g, i) =>
        i === groupIndex
          ? { ...g, conditions: [...g.conditions, makeCondition()] }
          : g,
      ),
    });
  };

  const removeCondition = (groupIndex: number, conditionIndex: number) => {
    emit({
      ...grouped,
      groups: groups.map((g, i) =>
        i === groupIndex
          ? {
              ...g,
              conditions: g.conditions.filter((_, ci) => ci !== conditionIndex),
            }
          : g,
      ),
    });
  };

  const addGroup = () => {
    emit({
      ...grouped,
      groups: [...groups, { operator: 'or', conditions: [makeCondition()] }],
    });
  };

  const removeGroup = (groupIndex: number) => {
    emit({
      ...grouped,
      groups: groups.filter((_, i) => i !== groupIndex),
    });
  };

  const handleHasAttachmentsChange = (value: string) => {
    const next = value === 'yes' ? true : value === 'no' ? false : null;
    emit({ ...grouped, hasAttachments: next });
  };

  return (
    <div className="space-y-2">
      {groups.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            <T context="label before top-level email filter group operator">
              Match
            </T>
          </span>
          <Select
            value={operator}
            onValueChange={(v) => {
              if (isBooleanOperator(v)) setTopOperator(v);
            }}
          >
            <SelectTrigger size="sm" className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="and">
                <T context="combine email filter groups with AND">
                  ALL groups (AND)
                </T>
              </SelectItem>
              <SelectItem value="or">
                <T context="combine email filter groups with OR">
                  ANY group (OR)
                </T>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {groups.map((group, groupIndex) => (
        <div
          key={groupIndex}
          className="space-y-1.5 rounded-md border bg-muted/30 p-3"
        >
          <div className="flex items-center justify-between gap-2">
            {group.conditions.length > 1 ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  <T context="label before email filter condition operator">
                    Match
                  </T>
                </span>
                <Select
                  value={group.operator}
                  onValueChange={(v) => {
                    if (isBooleanOperator(v)) setGroupOperator(groupIndex, v);
                  }}
                >
                  <SelectTrigger size="sm" className="w-[190px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="and">
                      <T context="combine email filter conditions with AND">
                        All conditions (AND)
                      </T>
                    </SelectItem>
                    <SelectItem value="or">
                      <T context="combine email filter conditions with OR">
                        Any condition (OR)
                      </T>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <span />
            )}

            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
              onClick={() => removeGroup(groupIndex)}
              aria-label={gt('Remove group')}
            >
              <XIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>

          {group.conditions.map((condition, conditionIndex) => (
            <div key={conditionIndex} className="flex items-center gap-2">
              <Select
                value={condition.field}
                onValueChange={(v) => {
                  if (isFilterableField(v)) {
                    updateCondition(groupIndex, conditionIndex, { field: v });
                  }
                }}
              >
                <SelectTrigger
                  size="sm"
                  className="w-[110px] shrink-0"
                  aria-label={gt('Field')}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FILTERABLE_FIELDS.map(({ key }) => (
                    <SelectItem key={key} value={key}>
                      {fieldLabels[key]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={condition.matchType}
                onValueChange={(v) => {
                  if (isMatchType(v)) {
                    updateCondition(groupIndex, conditionIndex, {
                      matchType: v,
                    });
                  }
                }}
              >
                <SelectTrigger
                  size="sm"
                  className="w-[160px] shrink-0"
                  aria-label={gt('Match type')}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contains">
                    <T>contains</T>
                  </SelectItem>
                  <SelectItem value="notContains">
                    <T>does not contain</T>
                  </SelectItem>
                </SelectContent>
              </Select>

              <Input
                className="h-8 flex-1"
                value={condition.value}
                onChange={(e) =>
                  updateCondition(groupIndex, conditionIndex, {
                    value: e.target.value,
                  })
                }
                placeholder={
                  condition.field === 'subject'
                    ? gt('keyword...')
                    : gt('email@example.com')
                }
              />

              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() => removeCondition(groupIndex, conditionIndex)}
                aria-label={gt('Remove condition')}
              >
                <XIcon className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </div>
          ))}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7"
            onClick={() => addCondition(groupIndex)}
          >
            <PlusIcon className="mr-1.5 h-3.5 w-3.5" />
            <T context="add a condition to an email filter group">
              Add condition
            </T>
          </Button>
        </div>
      ))}

      <Button type="button" variant="outline" size="sm" onClick={addGroup}>
        <PlusIcon className="mr-1.5 h-3.5 w-3.5" />
        <T context="add a new email filter group">Add group</T>
      </Button>

      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">
          <T>Attachments:</T>
        </span>
        <Select
          value={
            hasAttachments === true
              ? 'yes'
              : hasAttachments === false
                ? 'no'
                : 'any'
          }
          onValueChange={handleHasAttachmentsChange}
        >
          <SelectTrigger size="sm" className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">
              <T>Any</T>
            </SelectItem>
            <SelectItem value="yes">
              <T>Has attachments</T>
            </SelectItem>
            <SelectItem value="no">
              <T>No attachments</T>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
